import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import listSalons from 'actions/listSalons.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { USE_CUSTOM_SETUP } from 'config/use.conf';
import LINK from 'constants/link.const';
import { selectCurrentSalon } from 'selectors/currentSalon.selectors';
import {
  selectOnboardingRoles,
  selectOnboardingServices,
  selectOnboardingStylists,
} from 'selectors/onboarding.selectors';
import { selectSalonListObject } from 'selectors/salonInfo.selectors';
import { COGNITO_ATTRIBUTE, selectCognitoState, selectCognitoUserIdPayload } from 'utils/cognito.util';
import fromObjectToQueryString from 'utils/converters/object/fromObjectToQueryString.util';
import { fromPropsToQuery } from 'utils/converters/props/path.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isValidId } from 'utils/predicates/fields.util';

import HeaderContainer from 'onboarding/common/Header';
import OnboardingFooter from 'onboarding/common/OnboardingFooter';
import { getRoles } from 'onboarding/roles/actions';
import SalonSetupType from 'onboarding/salonSetupType/SalonSetupType';
import { getCategories, markCategoriesAsFetched } from 'onboarding/services/actions';

const L = logger('SalonSetupTypeWrap');

const calculateNextPathname = ({ salonId, salon, categories, roles, stylists }) => {
  const id = isValidId(salonId) ? salonId : asObject(salon).id;

  if (!isFullArray(roles)) {
    return '/signup/roles';
  }

  // if there is no categories go and create some
  if (!isFullArray(categories)) {
    return '/signup/categories';
  }

  // if there are categories get all services for them
  const services = categories.reduce((services, category) => services.concat(category.services), []);

  // if there are no services at all go and create some
  if (!isFullArray(services)) {
    return '/signup/services';
  }

  // if there is no stylist go to the page to create one
  if (!isFullArray(stylists)) {
    return '/signup/stylists';
  }

  // there is at least 1 stylist, end it
  return `/salon/${id}/day/today`;
};

const salonDataLoadingInit = {
  salonsLoading: true,
  specificSalonLoading: false,
  rolesLoaded: false,
  categoriesLoaded: false,
  stylistsLoaded: false,
};

class SalonSetupTypeWrap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isFooterButtonActive: false,
      showHideBorder: 'no-border',
      active: 0,
      salonDataLoading: { ...salonDataLoadingInit, categoriesLoaded: this.props.categories.areCategoriesFetched },
    };
  }

  // noinspection JSDeprecatedSymbols
  UNSAFE_componentWillMount() {
    this.props.listSalons();
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    // there is only customized setup, click it
    this.onCustomizedClick();
  }

  // noinspection JSCheckFunctionSignatures,JSDeprecatedSymbols
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { salonDataLoading: salonDataLoadingFromState } = this.state;
    const {
      getRoles,
      listSalons,
      fetchSalon,
      stylists: prevStylists,
      getStylists,
      getCategories,
      location,
      cognitoIdPayload: prevIdPayload,
      categories: prevCategories,
      roles: prevRoles,
    } = this.props;

    const {
      roles: nextRoles,
      stylists: nextStylists,
      salonList,
      cognitoIdPayload: nextIdPayload,
      categories: nextCategories,
    } = nextProps;

    const nextManaged = nextIdPayload?.[COGNITO_ATTRIBUTE.salonsManage];
    const nextAccess = nextIdPayload?.[COGNITO_ATTRIBUTE.salonsAccess];

    const prevManaged = prevIdPayload?.[COGNITO_ATTRIBUTE.salonsManage];
    const prevAccess = prevIdPayload?.[COGNITO_ATTRIBUTE.salonsAccess];

    if (nextManaged !== prevManaged || prevAccess !== nextAccess) {
      L.unignore('componentWillReceiveProps()', 'listSalons:', listSalons());
      this.setState({ salonDataLoading: salonDataLoadingInit });
    }

    if (
      salonDataLoadingFromState.salonsLoading &&
      salonList.response &&
      salonList.response.manager &&
      ((!salonList.isFetching && salonList.response.manager.length > 0) ||
        (!salonList.isFetching && salonList.errorMessage))
    ) {
      const salonDataLoading = Object.assign({}, salonDataLoadingFromState);
      salonDataLoading.salonsLoading = false;

      if (salonList.response.manager.length > 0) {
        let salonDetails;
        // We'll use this to load all the data for specific salon until we finally refactor the functions
        if (location.state && location.state.loadSalon) {
          salonDetails = location.state.loadSalon;
        } else {
          [salonDetails] = salonList.response.manager;
        }
        salonDataLoading.specificSalonLoading = true;
        if (salonDetails.id) {
          fetchSalon(salonDetails.id);
          getCategories(salonDetails.id);
          getRoles(salonDetails.id);
          getStylists(salonDetails.id);
        }
      }
      this.setState({ salonDataLoading });
    } else if (
      salonDataLoadingFromState.salonsLoading &&
      salonList.response &&
      salonList.response.receptionist &&
      ((!salonList.isFetching && salonList.response.receptionist.length > 0) ||
        (!salonList.isFetching && salonList.errorMessage))
    ) {
      const salonDataLoading = Object.assign({}, salonDataLoadingFromState);
      salonDataLoading.salonsLoading = false;

      if (salonList.response.receptionist.length > 0) {
        let salonDetails;
        // We'll use this to load all the data for specific salon until we finally refactor the functions
        if (location.state && location.state.loadSalon) {
          salonDetails = location.state.loadSalon;
        } else {
          [salonDetails] = salonList.response.receptionist;
        }
        salonDataLoading.specificSalonLoading = true;
        if (salonDetails.id) {
          fetchSalon(salonDetails.id);
          getCategories(salonDetails.id);
          getRoles(salonDetails.id);
          getStylists(salonDetails.id);
        }
      }
      this.setState({ salonDataLoading });
    } else if (
      salonDataLoadingFromState.salonsLoading &&
      salonList.response &&
      salonList.response.receptionist &&
      ((!salonList.isFetching &&
        salonList.response.receptionist.length === 0 &&
        salonList.response.manager.length === 0) ||
        (!salonList.isFetching && salonList.errorMessage))
    ) {
      this.setState({ salonDataLoading: { ...salonDataLoadingFromState, salonsLoading: false } });
    }

    if (
      salonDataLoadingFromState.specificSalonLoading &&
      !prevCategories.areCategoriesFetched &&
      nextCategories.areCategoriesFetched
    ) {
      const salonDataLoading = Object.assign({}, salonDataLoadingFromState);
      salonDataLoading.categoriesLoaded = true;
      this.setState({ salonDataLoading });
    }

    if (salonDataLoadingFromState.specificSalonLoading && prevRoles.isFetching && !nextRoles.isFetching) {
      const salonDataLoading = Object.assign({}, salonDataLoadingFromState);
      salonDataLoading.rolesLoaded = true;
      this.setState({ salonDataLoading });
    }

    if (salonDataLoadingFromState.specificSalonLoading && prevStylists.isFetching && !nextStylists.isFetching) {
      const salonDataLoading = Object.assign({}, salonDataLoadingFromState);
      salonDataLoading.stylistsLoaded = true;
      this.setState({ salonDataLoading });
    }
  }

  // noinspection JSCheckFunctionSignatures
  componentDidUpdate() {
    const {
      location,
      currentSalon: salon,
      stylists: stylistsFromProps,
      categories: categoriesFromProps,
      roles: rolesFromProps,
    } = this.props;
    const {
      salonDataLoading: { categoriesLoaded, rolesLoaded, specificSalonLoading, stylistsLoaded },
    } = this.state;

    // if everything is loaded, it's time to go to the last step that has not been completed

    if (salon && specificSalonLoading && categoriesLoaded && rolesLoaded && stylistsLoaded) {
      // if we are using the functionality that loads a specific salon,
      // we can tell the page after it loads where to redirect to
      const { redirectTo } = asObject(location.state);
      const query = fromPropsToQuery(this.props);

      if (redirectTo) {
        navFor('SalonSetupTypeWrap.componentDidUpdate()', this.props).push({
          pathname: redirectTo,
          search: fromObjectToQueryString(query),
        });
      } else {
        const { categories } = categoriesFromProps;
        const { roles } = rolesFromProps;
        const { stylists } = stylistsFromProps;

        navFor('SalonSetupTypeWrap.componentDidUpdate()', this.props).push({
          pathname: calculateNextPathname({ salonId: query.salonId, salon, categories, roles, stylists }),
          search: fromObjectToQueryString(query),
        });
      }
    }
  }

  onItemClick = () => {
    const cssClass = this.state.showHideBorder === 'no-border' ? 'active-border' : 'no-border';
    this.setState({ showHideBorder: cssClass });
  };

  onCustomizedClick = () => void this.setState({ active: 2 }, () => void this.checkButton());
  onQuickSetupClick = () => void this.setState({ active: 1 }, () => void this.checkButton());

  onFooterButtonClicked = () => {
    const { active } = this.state;
    const state = { normalFlow: true };

    const pathname =
      // eslint-disable-next-line no-nested-ternary
      !USE_CUSTOM_SETUP || active === 1 ? LINK.onboardingQuickType : active === 2 ? LINK.onboardingSalon : undefined;

    if (pathname) {
      navFor('SalonSetupTypeWrap.onFooterButtonClicked()', this.props).push({
        pathname,
        state,
        search: fromObjectToQueryString({ ...fromPropsToQuery(this.props), ...state }),
      });
    }
  };

  checkButton = () => void this.setState({ isFooterButtonActive: this.state.active !== 0 });

  render() {
    const {
      salonDataLoading: { salonsLoading, specificSalonLoading },
      isFooterButtonActive,
      active,
    } = this.state;

    return (
      <div data-bem="SalonSetupTypeWrap" className="content-wrap">
        {(salonsLoading || specificSalonLoading) && (
          <div className="lds-wrap">
            <div className="lds-ring">
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>
        )}

        <HeaderContainer
          titleFirstPart="Great, let's get your salon set up!"
          userName=""
          titleSecondPart=""
          description=""
        />

        <SalonSetupType
          onCustomizedClick={this.onCustomizedClick}
          onQuickSetupClick={this.onQuickSetupClick}
          onItemClick={this.onItemClick}
          active={active}
        />

        <OnboardingFooter
          isFooterButtonActive={isFooterButtonActive}
          onButtonClick={this.onFooterButtonClicked}
          isLoading={false}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  cognito: selectCognitoState(state),
  cognitoIdPayload: selectCognitoUserIdPayload(state),
  //
  currentSalon: selectCurrentSalon(state),
  salonList: selectSalonListObject(state),
  //
  roles: selectOnboardingRoles(state),
  categories: selectOnboardingServices(state),
  stylists: selectOnboardingStylists(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { listSalons, fetchSalon, getCategories, getRoles, getStylists: fetchStylistsBySalon, markCategoriesAsFetched },
    dispatch,
  );

const SalonSetupTypeWrapHoc = connect(mapStateToProps, mapDispatchToProps)(SalonSetupTypeWrap);

export default SalonSetupTypeWrapHoc;
